import React, { useEffect, useMemo, useState } from 'react';
import formAttachmentService, { type FormAttachment } from '../../../services/formAttachmentService';
import FormPictures from '../../forms/components/FormPictures';
import type { HiredEquipmentGridRow } from '../types';

export type HiredEquipmentTaskPictureLine = Pick<
  HiredEquipmentGridRow,
  'clientId' | 'taskGroupId' | 'taskLabel' | 'resourceLabel'
>;

interface HiredEquipmentTaskPicturesProps {
  formId: string;
  rows: HiredEquipmentTaskPictureLine[];
}

function lineHeading(row: HiredEquipmentTaskPictureLine, lineNumber: number): string {
  const task = row.taskLabel?.trim() || 'Task';
  const resource = row.resourceLabel?.trim();
  return resource ? `Line ${lineNumber} — ${task} — ${resource}` : `Line ${lineNumber} — ${task}`;
}

const HiredEquipmentTaskPictures: React.FC<HiredEquipmentTaskPicturesProps> = ({ formId, rows }) => {
  const [attachmentsByTask, setAttachmentsByTask] = useState<Record<string, FormAttachment[]>>({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const lines = useMemo(
    () =>
      rows
        .map((row, index) => ({ row, lineNumber: index + 1 }))
        .filter((entry): entry is { row: HiredEquipmentTaskPictureLine & { taskGroupId: string }; lineNumber: number } =>
          Boolean(entry.row.taskGroupId),
        ),
    [rows],
  );

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);
    void formAttachmentService
      .getFormAttachmentsByTask(formId)
      .then((grouped) => {
        if (!cancelled) setAttachmentsByTask(grouped);
      })
      .catch(() => {
        if (!cancelled) setError('Failed to load pictures');
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [formId]);

  if (lines.length === 0) return null;

  if (loading) {
    return (
      <div className="tw-mt-4 tw-rounded-lg tw-border tw-border-line tw-bg-surface tw-p-4">
        <p className="tw-mb-0 tw-text-sm tw-text-content-muted">Loading pictures…</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="tw-mt-4 tw-rounded-lg tw-border tw-border-danger/30 tw-bg-danger/10 tw-p-4">
        <p className="tw-mb-0 tw-text-sm tw-text-danger">{error}</p>
      </div>
    );
  }

  const sections = lines
    .map(({ row, lineNumber }) => {
      const attachments = attachmentsByTask[`task-${row.taskGroupId}`] ?? [];
      if (attachments.length === 0) return null;
      return (
        <div key={row.clientId} className="tw-mt-3">
          <FormPictures
            formId={formId}
            attachmentsOverride={attachments}
            showOnlyWhenExpanded={false}
            isExpanded={true}
            hideWhenEmpty={true}
            title={lineHeading(row, lineNumber)}
          />
        </div>
      );
    })
    .filter(Boolean);

  if (sections.length === 0) return null;

  return (
    <div className="tw-mt-4">
      <h3 className="tw-mb-2 tw-text-base tw-font-semibold tw-text-content">Pictures by line</h3>
      {sections}
    </div>
  );
};

export default HiredEquipmentTaskPictures;
